import {
  AutoDiscoverOrbitLedRequest,
  IOrbitLedHubInfo,
  IOrbitLedInfo,
  IOrbitLedUpdateRequest,
  OrbitLedEndpoints,
  OrbitLedInfo,
} from '@scaleaq/sitecontrol-lib';
import { useMutation } from '@tanstack/react-query';

import { API_NAMES, CACHE_KEYS } from '@/constants';
import {
  useAuthApiDeleteById,
  useAuthApiGetMultiFetch,
  useAuthApiPost,
  useAuthApiUpdateManyDevicesById,
  type ServiceQueryOptions,
} from '@/dataAccess/rest/apiHooks';

// Keep this in case someone insists using this method instead of getting the list using useGetAllLedHubs
// export const useGetAllLedDevices = () => {
//   return useAuthApiGetMultiFetch<IOrbitLedInfo>({
//     baseApiRoute: API_NAMES.orbitLedApi,
//     cacheKeys: [CACHE_KEYS.allOrbitLedDevices],
//     routesArray: [OrbitLedEndpoints.getAllLedDevices],
//   });
// };

/**
 *
 * @param identifier - typically the hub id
 */
export const usePostLedHubAutoDiscoverRequest = (identifier?: string) => {
  return useAuthApiPost<AutoDiscoverOrbitLedRequest, OrbitLedInfo[]>({
    baseApiRoute: API_NAMES.orbitLedApi,
    cacheKeys: [CACHE_KEYS.allOrbitLedHubDevices],
    identifier: identifier || 'useLedHubAutodiscover',
    returnJsonResult: true,
    route: OrbitLedEndpoints.autoDiscoverLed,
  });
};

export const useGetAllLedHubs = ({ enabled }: ServiceQueryOptions = {}) => {
  return useAuthApiGetMultiFetch<IOrbitLedHubInfo>({
    baseApiRoute: API_NAMES.orbitLedApi,
    cacheKeys: [CACHE_KEYS.allOrbitLedHubDevices],
    routesArray: [OrbitLedEndpoints.getAllHubDevices],
    enabled,
  });
};

export const useAddOrbitLed = (identifier?: string) => {
  return useAuthApiPost({
    baseApiRoute: API_NAMES.orbitLedApi,
    route: OrbitLedEndpoints.addLed,
    cacheKeys: [CACHE_KEYS.allOrbitLedHubDevices],
    identifier: identifier,
  });
};

export const useAutoDiscoverOrbitLed = (identifier?: string) => {
  return useAuthApiPost<AutoDiscoverOrbitLedRequest, IOrbitLedInfo[]>({
    baseApiRoute: API_NAMES.orbitLedApi,
    cacheKeys: [CACHE_KEYS.allOrbitLedHubDevices],
    identifier: identifier,
    returnJsonResult: true,
    route: OrbitLedEndpoints.autoDiscoverLed,
  });
};

export const useModifyManyOrbitLeds = () => {
  return useAuthApiUpdateManyDevicesById<IOrbitLedUpdateRequest & { id: string }>({
    baseApiRoute: API_NAMES.orbitLedApi,
    route: OrbitLedEndpoints.updateLedById,
    cacheKeys: [CACHE_KEYS.allOrbitLedHubDevices],
    putOrPatch: 'PATCH',
  });
};
export const useRemoveOrbitLedById = (identifier?: string) => {
  return useAuthApiDeleteById({
    baseApiRoute: API_NAMES.orbitLedApi,
    cacheKeys: [CACHE_KEYS.allOrbitLedHubDevices],
    identifier: identifier || 'useRemoveOrbitLedById',
    route: OrbitLedEndpoints.removeLedById,
  });
};

/**
 * Mock: backend endpoint for removing an Orbit LED hub is not yet available.
 * Throws so the caller can surface a "not implemented" error to the user.
 */
export const useRemoveOrbitLedHubById = () => {
  return useMutation<void, Error, IOrbitLedHubInfo>({
    mutationFn: async () => {
      throw new Error('useRemoveOrbitLedHubById is not implemented');
    },
  });
};
